import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapboxOverlay } from "@deck.gl/mapbox";
import { ScatterplotLayer, ArcLayer, ColumnLayer, TextLayer } from "@deck.gl/layers";
import { HexagonLayer } from "@deck.gl/aggregation-layers";
import { TripsLayer } from "@deck.gl/geo-layers";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Minus, Crosshair, Globe2, Map as MapIcon, Layers, Box, AlertTriangle } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useUserLocation } from "@/lib/location";

// ─────────────────────────── constantes
export const QROO_CENTER = [20.2, -87.6];
const STYLE = {
  dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  light: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
};
export const STATUS_RGB = {
  recibido: [95, 211, 224], validado: [46, 230, 200], asignado: [242, 193, 78], en_proceso: [64, 156, 255],
  resuelto: [120, 232, 140], cerrado: [160, 176, 190], rechazado: [255, 90, 106],
};
export const STATUS_LABEL = {
  recibido: "Recibido", validado: "Validado", asignado: "Asignado", en_proceso: "En proceso",
  resuelto: "Resuelto", cerrado: "Cerrado", rechazado: "Rechazado",
};
// Sedes municipales (destino de los arcos de turnado)
const SEATS = [
  ["Cancún", 21.1619, -86.8515], ["Chetumal", 18.5001, -88.2961], ["Playa del Carmen", 20.6296, -87.0739], ["Tulum", 20.2114, -87.4654],
  ["Cozumel", 20.5083, -86.9458], ["Isla Mujeres", 21.2311, -86.731], ["F. Carrillo Puerto", 19.5786, -88.0453], ["Bacalar", 18.677, -88.3953],
];
const DEP_OFFSET = { obras: [0.012, 0.01], servicios: [-0.012, 0.012], capa: [0.014, -0.01], seguridad: [-0.01, -0.012],
  ecologia: [0.004, 0.018], transito: [-0.016, 0], tesoreria: [0.018, 0.002], desarrollo: [0, -0.018] };
const HEX_RANGE = [[18, 72, 84], [0, 124, 145], [46, 190, 205], [95, 211, 224], [242, 193, 78], [255, 90, 106]];
const CYAN = [95, 211, 224];

const nearestSeat = (lat, lng) => {
  let best = SEATS[0], bd = Infinity;
  for (const s of SEATS) { const d = (s[1] - lat) ** 2 + (s[2] - lng) ** 2; if (d < bd) { bd = d; best = s; } }
  return best;
};
const arcPath = (a, b, steps = 28) => {
  const dist = Math.hypot(b[0] - a[0], b[1] - a[1]) * 111000;
  const h = Math.max(2500, dist * 0.35);
  const out = [];
  for (let i = 0; i <= steps; i++) {
    const k = i / steps;
    out.push([a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, Math.sin(Math.PI * k) * h]);
  }
  return out;
};
const hash = (s) => { let h = 0; for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return Math.abs(h % 1000) / 1000; };
// Proyecci\u00f3n autom\u00e1tica con hist\u00e9resis (evita parpadeo en el umbral)
const autoProj = (zoom, current) => (current === "globe" ? (zoom > 4.8 ? "mercator" : "globe") : (zoom < 4.2 ? "globe" : "mercator"));
const debtRGB = (amt) => (amt > 18000 ? [255, 90, 106] : amt > 10000 ? [255, 138, 80] : amt > 5000 ? [242, 193, 78] : [95, 211, 224]);
let WEBGL2 = null;
const webglOK = () => {
  if (WEBGL2 !== null) return WEBGL2;
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2");
    WEBGL2 = !!gl;
    gl?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch { WEBGL2 = false; }
  return WEBGL2;
};

const LAYER_META = {
  pulses: { label: "Pulsos de reportes", hint: "Anillos por estatus" },
  arcs: { label: "Arcos de turnado", hint: "Reporte → dependencia" },
  hex: { label: "Columnas 3D", hint: "Concentración por zona" },
  trips: { label: "Rutas de cuadrillas", hint: "Estelas en movimiento" },
  debts: { label: "Columnas de adeudo", hint: "Monto por cuenta" },
};

/**
 * GeoMap · elOS — globo MapLibre v5 + basemap Carto + capas animadas deck.gl.
 * API declarativa: reports, hexData, crews, debts, picked, onPick, onSelectReport, flyTo.
 */
export function GeoMap(props) {
  const { theme: appTheme } = useTheme();
  const theme = props.theme || appTheme;
  if (!webglOK()) {
    return (
      <div className={`geo-shell relative w-full overflow-hidden rounded-xl border flex items-center justify-center ${props.className || "h-[360px]"}`} data-testid={props.testid || "geo-map"}>
        <div className="text-center text-sm text-muted-foreground px-6"><AlertTriangle className="h-5 w-5 mx-auto mb-2" />Tu navegador no soporta WebGL2: el globo 3D no puede mostrarse.</div>
      </div>
    );
  }
  return <GeoMapInner key={theme} {...props} theme={theme} />;
}

function GeoMapInner({
  center = QROO_CENTER, zoom = 6.2, pitch = 40, bearing = -12, className = "h-[360px]", testid = "geo-map",
  reports = [], hexData = null, crews = null, debts = null, arcs = true,
  picked = null, onPick = null, onSelectReport = null, flyTo = null, flyZoom = 16,
  defaultLayers = {}, hud = true, compact = false, interactive = true, scrollZoom = true, autoRotate = false,
  showUser = true, projection: projInit = "globe", theme = "dark", title = null, children,
}) {
  const wrap = useRef(null);
  const holder = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const t0 = useRef(performance.now());
  const interacted = useRef(false);
  const epoch = useRef(0);
  const curProj = useRef(projInit);
  const [ready, setReady] = useState(false);
  const [proj, setProj] = useState(projInit);
  const [coords, setCoords] = useState(null);
  const [z, setZ] = useState(zoom);
  const loc = useUserLocation();
  const user = showUser && loc?.coords ? loc.coords : null;

  const available = useMemo(() => ({
    pulses: reports?.length > 0,
    arcs: arcs && reports?.length > 0,
    hex: !!hexData,
    trips: !!crews,
    debts: !!debts,
  }), [reports?.length, arcs, hexData, crews, debts]);
  const [on, setOn] = useState(() => ({ pulses: true, arcs: true, hex: true, trips: true, debts: true, ...defaultLayers }));

  // datos derivados
  const pts = useMemo(() => (reports || []).filter((r) => r && r.lat != null && r.lng != null).map((r) => ({
    ...r, pos: [r.lng, r.lat], rgb: STATUS_RGB[r.status] || CYAN, ph: hash(r.folio || `${r.lat}${r.lng}`),
  })), [reports]);
  const arcData = useMemo(() => pts.filter((p) => !["cerrado", "rechazado"].includes(p.status)).map((p) => {
    const s = nearestSeat(p.lat, p.lng), o = DEP_OFFSET[p.dependency] || [0, 0];
    const target = [s[2] + o[1], s[1] + o[0]];
    const path = arcPath(p.pos, target);
    return { ...p, target, seat: s[0], path, ts: path.map((_, i) => i / (path.length - 1) + p.ph) };
  }), [pts]);
  const seatData = useMemo(() => {
    const used = new Set(arcData.map((a) => a.seat));
    return SEATS.filter((s) => used.has(s[0])).map((s) => ({ name: s[0], pos: [s[2], s[1]] }));
  }, [arcData]);
  const tripData = useMemo(() => (crews || []).map((c) => {
    const path = (c.trail?.length > 1 ? c.trail : [[c.lat - 0.004, c.lng - 0.004], [c.lat, c.lng]]).map(([la, ln]) => [ln, la]);
    return { ...c, path, ts: path.map((_, i) => i), len: path.length };
  }), [crews]);
  const hexPts = useMemo(() => (hexData || []).filter((p) => p.lat != null).map((p) => ({ ...p, pos: [p.lng, p.lat] })), [hexData]);
  const debtPts = useMemo(() => (debts || []).filter((p) => p.lat != null).map((p) => ({ ...p, pos: [p.lng, p.lat], rgb: debtRGB(p.amount) })), [debts]);

  const live = useRef({});
  live.current = { pts, arcData, seatData, tripData, hexPts, debtPts, picked, user, on, available, onSelectReport, proj, theme };

  // ─────────────── construcción de capas (por frame)
  const buildLayers = useCallback((t) => {
    const L = live.current, a = L.available, o = L.on, P = `${L.proj}-${epoch.current}`, dark = L.theme === "dark";
    const layers = [];
    const grow = Math.min(1, t / 1.6), ease = 1 - (1 - grow) ** 3;
    if (a.hex && o.hex && L.hexPts.length) {
      layers.push(new HexagonLayer({
        id: `hex-${P}`, data: L.hexPts, getPosition: (d) => d.pos, radius: 5200, coverage: 0.86, extruded: true,
        elevationScale: 30 * ease, elevationRange: [0, 1600], colorRange: HEX_RANGE, upperPercentile: 100, opacity: 0.78,
        pickable: true, material: { ambient: 0.6, diffuse: 0.6, shininess: 40 },
        updateTriggers: { elevationScale: ease },
      }));
    }
    if (a.debts && o.debts && L.debtPts.length) {
      layers.push(new ColumnLayer({
        id: `debts-${P}`, data: L.debtPts, getPosition: (d) => d.pos, diskResolution: 6, radius: 1400, extruded: true,
        getElevation: (d) => Math.sqrt(d.amount) * 45 * ease, getFillColor: (d) => [...d.rgb, 210], pickable: true, opacity: 0.85,
        updateTriggers: { getElevation: ease },
      }));
    }
    if (a.arcs && o.arcs && L.arcData.length) {
      layers.push(new ArcLayer({
        id: `arcs-${P}`, data: L.arcData, getSourcePosition: (d) => d.pos, getTargetPosition: (d) => d.target,
        getSourceColor: (d) => [...d.rgb, 70], getTargetColor: [...CYAN, 90], getWidth: 1, getHeight: 0.35, widthMinPixels: 1,
      }));
      layers.push(new TripsLayer({
        id: `arc-packets-${P}`, data: L.arcData, getPath: (d) => d.path, getTimestamps: (d) => d.ts,
        getColor: (d) => d.rgb, widthMinPixels: 3, capRounded: true, jointRounded: true, trailLength: 0.32,
        currentTime: (t * 0.32) % 2.2, fadeTrail: true, opacity: 0.9,
      }));
      layers.push(new ScatterplotLayer({
        id: `seats-${P}`, data: L.seatData, getPosition: (d) => d.pos, radiusUnits: "pixels",
        getRadius: 5 + Math.sin(t * 3) * 1.2, filled: true, stroked: true, getFillColor: dark ? [6, 21, 29, 255] : [255, 255, 255, 255],
        getLineColor: CYAN, lineWidthMinPixels: 2, updateTriggers: { getRadius: t },
      }));
      layers.push(new TextLayer({
        id: `seat-labels-${P}`, data: L.seatData, getPosition: (d) => d.pos, getText: (d) => d.name.toUpperCase(), characterSet: "auto",
        getSize: 11, getColor: dark ? [185, 231, 238, 230] : [15, 76, 92, 230], getPixelOffset: [10, -10], getTextAnchor: "start",
        fontFamily: "IBM Plex Mono, monospace", fontWeight: 600, outlineWidth: dark ? 0 : 0,
      }));
    }
    if (a.trips && o.trips && L.tripData.length) {
      const maxLen = Math.max(...L.tripData.map((d) => d.len));
      layers.push(new TripsLayer({
        id: `trips-${P}`, data: L.tripData, getPath: (d) => d.path, getTimestamps: (d) => d.ts,
        getColor: [46, 230, 200], widthMinPixels: 4, capRounded: true, jointRounded: true, trailLength: Math.max(4, maxLen * 0.6),
        currentTime: (t * 2.4) % (maxLen + 3), fadeTrail: true,
      }));
      layers.push(new ScatterplotLayer({
        id: `crew-halo-${P}`, data: L.tripData, getPosition: (d) => d.path[d.path.length - 1], radiusUnits: "pixels",
        getRadius: 10 + ((t * 0.9) % 1) * 16, filled: false, stroked: true, getLineColor: [46, 230, 200, 255 * (1 - ((t * 0.9) % 1))],
        lineWidthMinPixels: 1.5, updateTriggers: { getRadius: t, getLineColor: t },
      }));
      layers.push(new ScatterplotLayer({
        id: `crews-${P}`, data: L.tripData, getPosition: (d) => d.path[d.path.length - 1], radiusUnits: "pixels", getRadius: 7,
        filled: true, stroked: true, getFillColor: [6, 21, 29], getLineColor: [46, 230, 200], lineWidthMinPixels: 2.5, pickable: true,
      }));
      layers.push(new TextLayer({
        id: `crew-labels-${P}`, data: L.tripData, getPosition: (d) => d.path[d.path.length - 1], getText: (d) => d.name || "", characterSet: "auto",
        getSize: 12, getColor: dark ? [232, 246, 249, 240] : [15, 40, 50, 240], getPixelOffset: [0, -18], fontFamily: "Work Sans, sans-serif", fontWeight: 600,
      }));
    }
    if (a.pulses && o.pulses && L.pts.length) {
      layers.push(new ScatterplotLayer({
        id: `pulse-${P}`, data: L.pts, getPosition: (d) => d.pos, radiusUnits: "pixels",
        getRadius: (d) => 5 + ((t * 0.55 + d.ph) % 1) * 22, filled: false, stroked: true,
        getLineColor: (d) => [...d.rgb, 230 * (1 - ((t * 0.55 + d.ph) % 1))], lineWidthMinPixels: 1.6,
        updateTriggers: { getRadius: t, getLineColor: t },
      }));
      layers.push(new ScatterplotLayer({
        id: `reports-${P}`, data: L.pts, getPosition: (d) => d.pos, radiusUnits: "pixels", getRadius: 5, radiusMinPixels: 4,
        filled: true, stroked: true, getFillColor: (d) => [...d.rgb, 255], getLineColor: dark ? [6, 21, 29, 255] : [255, 255, 255, 255],
        lineWidthMinPixels: 1.5, pickable: true, autoHighlight: true, highlightColor: [255, 255, 255, 200],
        onClick: ({ object }) => { if (object && live.current.onSelectReport) live.current.onSelectReport(object); return true; },
      }));
    }
    if (L.user) {
      const u = [L.user.lng, L.user.lat];
      layers.push(new ScatterplotLayer({
        id: `user-acc-${P}`, data: [u], getPosition: (d) => d, radiusUnits: "meters", getRadius: Math.max(8, L.user.accuracy || 20),
        filled: true, stroked: true, getFillColor: [95, 211, 224, 40], getLineColor: [95, 211, 224, 140], lineWidthMinPixels: 1,
      }));
      layers.push(new ScatterplotLayer({
        id: `user-${P}`, data: [u], getPosition: (d) => d, radiusUnits: "pixels", getRadius: 7 + Math.sin(t * 4) * 1.2,
        filled: true, stroked: true, getFillColor: [95, 211, 224], getLineColor: [255, 255, 255], lineWidthMinPixels: 3,
        updateTriggers: { getRadius: t },
      }));
    }
    if (L.picked) {
      const p = [L.picked[1], L.picked[0]], ph = (t * 0.8) % 1;
      layers.push(new ScatterplotLayer({
        id: `pick-halo-${P}`, data: [p], getPosition: (d) => d, radiusUnits: "pixels", getRadius: 10 + ph * 26,
        filled: false, stroked: true, getLineColor: [242, 193, 78, 255 * (1 - ph)], lineWidthMinPixels: 2, updateTriggers: { getRadius: t, getLineColor: t },
      }));
      layers.push(new ScatterplotLayer({
        id: `pick-${P}`, data: [p], getPosition: (d) => d, radiusUnits: "pixels", getRadius: 8,
        filled: true, stroked: true, getFillColor: [242, 193, 78], getLineColor: [255, 255, 255], lineWidthMinPixels: 3,
      }));
    }
    return layers;
  }, []);

  // ─────────────── montaje MapLibre + overlay deck.gl
  useEffect(() => {
    let map;
    try {
      map = new maplibregl.Map({
        container: holder.current, style: STYLE[theme] || STYLE.dark, center: [center[1], center[0]], zoom, pitch, bearing,
        maxPitch: 75, attributionControl: { compact: true }, interactive, scrollZoom: interactive && scrollZoom,
        cooperativeGestures: false, fadeDuration: 0,
      });
    } catch (e) { console.error("GeoMap init", e); return undefined; }
    mapRef.current = map;
    const overlay = new MapboxOverlay({
      interleaved: true, layers: [],
      getTooltip: ({ object, layer }) => tooltipFor(object, layer, theme),
    });
    overlayRef.current = overlay;
    holder.current.__hgOverlay = overlay; // depuración/QA
    const markInteract = () => { interacted.current = true; };
    map.on("mousedown", markInteract); map.on("touchstart", markInteract); map.on("wheel", markInteract);
    map.on("style.load", () => {
      const first = projInit === "mercator" ? "mercator" : autoProj(map.getZoom(), "globe");
      curProj.current = first;
      try { map.setProjection({ type: first }); } catch { /* noop */ }
      setProj(first);
      try {
        map.setSky(theme === "dark"
          ? { "sky-color": "#0A2730", "horizon-color": "#0E3A47", "fog-color": "#06151D", "sky-horizon-blend": 0.6, "horizon-fog-blend": 0.6, "fog-ground-blend": 0.4, "atmosphere-blend": ["interpolate", ["linear"], ["zoom"], 0, 1, 6, 0.8, 10, 0] }
          : { "sky-color": "#DFF3F6", "horizon-color": "#ffffff", "fog-color": "#F2F8FA", "atmosphere-blend": ["interpolate", ["linear"], ["zoom"], 0, 1, 6, 0.6, 10, 0] });
      } catch { /* noop */ }
      if (!map.hasControl?.(overlay)) map.addControl(overlay);
      // deck.gl: re-inicializa capas cuando la proyección globo ya está aplicada
      map.once("idle", () => { epoch.current += 1; });
      setTimeout(() => { epoch.current += 1; }, 600);
      setReady(true);
    });
    map.on("mousemove", (e) => setCoords([e.lngLat.lat, e.lngLat.lng]));
    map.on("zoomend", () => setZ(map.getZoom()));
    // Transición autom\u00e1tica: globo al alejar, vista 3D plana al acercar (capas deck.gl 3D completas)
    map.on("zoom", () => {
      if (projInit === "mercator") return;
      const next = autoProj(map.getZoom(), curProj.current);
      if (next !== curProj.current) {
        curProj.current = next;
        try { map.setProjection({ type: next }); } catch { /* noop */ }
        epoch.current += 1;
        setProj(next);
      }
    });
    map.on("click", (e) => { if (live.current.onPickFn) live.current.onPickFn([e.lngLat.lat, e.lngLat.lng]); });

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf, last = 0;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      if (now - last < 33) return; // ~30 fps
      last = now;
      const t = reduce ? 1.6 : (now - t0.current) / 1000;
      try { overlay.setProps({ layers: buildLayers(t) }); } catch { /* noop */ }
      if (autoRotate && !interacted.current && !reduce) {
        const c = map.getCenter();
        map.setCenter([c.lng + 0.012, c.lat]);
      }
    };
    raf = requestAnimationFrame(tick);
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(holder.current);
    return () => {
      cancelAnimationFrame(raf); ro.disconnect();
      try { map.removeControl(overlay); } catch { /* noop */ }
      map.remove(); mapRef.current = null; overlayRef.current = null;
    };
  }, []); // eslint-disable-line

  live.current.onPickFn = onPick;

  // fly-to externo
  useEffect(() => {
    if (flyTo && mapRef.current) mapRef.current.flyTo({ center: [flyTo[1], flyTo[0]], zoom: flyZoom, speed: 1.2, curve: 1.4, essential: true });
  }, [flyTo?.[0], flyTo?.[1]]); // eslint-disable-line

  // ─────────────── controles
  const m = () => mapRef.current;
  const zoomBy = (d) => m()?.easeTo({ zoom: m().getZoom() + d, duration: 250 });
  const reset = () => m()?.flyTo({ center: [center[1], center[0]], zoom, pitch, bearing, speed: 1.2, curve: 1.4 });
  const toggleProj = () => {
    // Globo \u2192 vuelve a la vista operativa; Plano \u2192 se aleja hasta ver el globo completo
    if (proj === "globe") reset();
    else m()?.flyTo({ center: [center[1], center[0]], zoom: 2.6, pitch: 0, bearing: 0, speed: 1.1, curve: 1.6 });
  };
  const togglePitch = () => m()?.easeTo({ pitch: m().getPitch() > 10 ? 0 : 50, duration: 400 });
  const layerKeys = Object.keys(available).filter((k) => available[k]);
  const legend = pts.length > 0 && !compact;

  return (
    <div ref={wrap} className={`geo-shell relative w-full overflow-hidden rounded-xl border ${className}`} data-testid={testid} data-theme={theme}>
      <div ref={holder} style={{ position: "absolute", inset: 0 }} />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid={`${testid}-loading`}>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--hud-fg-dim)] flex items-center gap-2"><span className="live-dot h-1.5 w-1.5 rounded-full bg-[#5fd3e0]" />Iniciando globo…</div>
        </div>
      )}
      {hud && (
        <>
          {/* top-left: título + estado */}
          {!compact && (
            <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none">
              <span className="hud-chip" data-testid={`${testid}-title`}><span className="live-dot h-1.5 w-1.5 rounded-full bg-[#2EE6C8]" />{title || "elOS · Mapa vivo"}</span>
              <span className="hud-chip hidden sm:inline-flex">{proj === "globe" ? "Globo" : "Vista 3D"} · z{z.toFixed(1)}</span>
            </div>
          )}
          {/* top-right: capas */}
          {!compact && layerKeys.length > 0 && (
            <div className="absolute top-3 right-3">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="hud-btn px-3 gap-2" data-testid={`${testid}-layers-button`} aria-label="Capas del mapa"><Layers className="h-4 w-4" /><span className="text-xs font-medium">Capas</span></button>
                </PopoverTrigger>
                <PopoverContent align="end" className="hud-popover w-64 p-3" data-testid={`${testid}-layers-panel`}>
                  <div className="hud-label mb-2">Capas animadas · deck.gl</div>
                  <div className="space-y-2.5">
                    {layerKeys.map((k) => (
                      <label key={k} className="flex items-center justify-between gap-3 cursor-pointer">
                        <span><span className="block text-sm">{LAYER_META[k].label}</span><span className="block text-[11px] opacity-60">{LAYER_META[k].hint}</span></span>
                        <Switch checked={!!on[k]} onCheckedChange={(v) => setOn((s) => ({ ...s, [k]: v }))} data-testid={`${testid}-layer-${k}`} />
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
          {/* bottom-left: leyenda + coordenadas */}
          <div className="absolute bottom-3 left-3 flex flex-col gap-2 items-start max-w-[70%] pointer-events-none">
            {legend && (
              <div className="hud-panel px-2.5 py-1.5 flex flex-wrap gap-x-3 gap-y-1" data-testid={`${testid}-legend`}>
                {Object.entries(STATUS_LABEL).filter(([k]) => pts.some((p) => p.status === k)).map(([k, l]) => (
                  <span key={k} className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider">
                    <span className="h-2 w-2 rounded-full" style={{ background: `rgb(${STATUS_RGB[k].join(",")})`, boxShadow: `0 0 8px rgb(${STATUS_RGB[k].join(",")})` }} />{l}
                  </span>
                ))}
              </div>
            )}
            {!compact && interactive && (
              <div className="hud-chip font-mono" data-testid={`${testid}-coords`}>
                {coords ? `${coords[0].toFixed(5)}, ${coords[1].toFixed(5)}` : `${center[0].toFixed(3)}, ${center[1].toFixed(3)}`}
              </div>
            )}
          </div>
          {/* bottom-right: controles */}
          {interactive && (
            <TooltipProvider delayDuration={200}>
              <div className="absolute bottom-8 right-3 flex flex-col gap-1.5">
                {[
                  ["zoom-in", "Acercar", Plus, () => zoomBy(1)],
                  ["zoom-out", "Alejar", Minus, () => zoomBy(-1)],
                  ["reset", "Volver a la vista inicial", Crosshair, reset],
                  ...(!compact ? [["pitch", "Inclinación 3D", Box, togglePitch], ["projection", proj === "globe" ? "Volver a la vista operativa" : "Ver globo completo", proj === "globe" ? MapIcon : Globe2, toggleProj]] : []),
                ].map(([id, label, Icon, fn]) => (
                  <Tooltip key={id}>
                    <TooltipTrigger asChild>
                      <button type="button" onClick={fn} className="hud-btn h-9 w-9" aria-label={label} data-testid={`${testid}-${id}-button`}><Icon className="h-4 w-4" /></button>
                    </TooltipTrigger>
                    <TooltipContent side="left">{label}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          )}
        </>
      )}
      {onPick && <div className="absolute top-3 left-1/2 -translate-x-1/2 hud-chip pointer-events-none" data-testid={`${testid}-pick-hint`}>Toca el mapa para fijar el punto</div>}
      {children}
    </div>
  );
}

function tooltipFor(o, layer, theme) {
  if (!o || !layer) return null;
  const id = layer.id || "";
  const style = {
    background: theme === "dark" ? "rgba(6,24,32,0.92)" : "rgba(255,255,255,0.95)", color: theme === "dark" ? "#E8F6F9" : "#16202B",
    border: "1px solid rgba(127,214,226,0.35)", borderRadius: "10px", padding: "8px 10px", fontFamily: "Work Sans, sans-serif", fontSize: "12px",
    boxShadow: "0 12px 30px -12px rgba(0,124,145,0.6)", backdropFilter: "blur(10px)",
  };
  const mono = "font-family:'IBM Plex Mono',monospace;font-size:11px;opacity:.75";
  if (id.startsWith("reports-")) {
    return { html: `<div style="${mono}">${o.folio || ""}</div><div style="font-weight:600">${o.category_label || "Reporte"}</div><div style="opacity:.7">${[o.colonia, o.municipio].filter(Boolean).join(" · ")}</div><div style="margin-top:4px;color:rgb(${o.rgb.join(",")})">● ${STATUS_LABEL[o.status] || o.status}</div>`, style };
  }
  if (id.startsWith("hex-")) {
    const n = o.count ?? o.points?.length ?? o.colorValue ?? 0;
    return { html: `<div style="${mono}">ZONA</div><div style="font-weight:600">${Math.round(n)} solicitud(es)</div>`, style };
  }
  if (id.startsWith("debts-")) {
    return { html: `<div style="${mono}">${o.account || ""}</div><div style="font-weight:600">${o.type || "Adeudo"}</div><div>$${Math.round(o.amount).toLocaleString("es-MX")}</div>`, style };
  }
  if (id.startsWith("crews-")) {
    return { html: `<div style="${mono}">CUADRILLA</div><div style="font-weight:600">${o.name || ""}</div>${o.collected_today != null ? `<div>Hoy: $${Math.round(o.collected_today).toLocaleString("es-MX")}</div>` : ""}`, style };
  }
  return null;
}

export default GeoMap;
